import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UpdateIcon from '@mui/icons-material/Update';
import InventoryIcon from '@mui/icons-material/Inventory';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useCatalogContext } from './CatalogContext';

const CatalogHeader = () => {
  const {
    viewMode,
    handleViewModeChange,
    totalCount,
    produtos,
    loading,
    handleCreateProduto,
    handleImportCmv, // Adicionando referência à nova função
    handleSortMenuOpen,
    handleSortMenuClose,
    handleSortChange,
    currentSort,
    sortAnchorEl,
    batchActionAnchorEl,
    setBatchActionAnchorEl,
    additionalMenuAnchorEl,
    handleAdditionalMenuOpen,
    handleAdditionalMenuClose,
    handleBatchAction,
    handleDrawerToggle
  } = useCatalogContext();

  const isMobile = window.innerWidth < 600;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        mb: 3,
        pb: 2,
        borderBottom: '1px solid #f0f0f0'
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Gerenciamento de Produtos
          </Typography>

          <Chip
            label={loading ? "Carregando..." : `${totalCount} itens`}
            size="small"
            sx={{
              bgcolor: '#fff3e0',
              color: '#ff9800',
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 24
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.8, gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {totalCount > 0 && !loading ?
              `Mostrando ${produtos.length} de ${totalCount} produtos` :
              '\u00A0' /* Espaço não-quebrável para manter a altura consistente */}
          </Typography>

          {!loading && produtos.length > 0 && (
            <>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                {produtos.filter(p => p.isProductActive).length} ativos
              </Typography>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                <span>Canal: </span>
                <Chip
                  label="Mercado Livre"
                  size="small"
                  sx={{
                    ml: 0.5,
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    bgcolor: '#fff3e0',
                    color: '#ff9800'
                  }}
                />
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ mt: { xs: 2, md: 0 }, display: 'flex', gap: 1, flexWrap: 'wrap' }}>        {/* Botões de ação primária */}
        <Tooltip title="Em Desenvolvimento" arrow>
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateProduto}
              sx={{
                bgcolor: "#ff9800",
                color: "white",
                fontWeight: 600,
                "&:hover": { bgcolor: "#f57c00" },
                order: { xs: -2, md: 4 },
                display: { xs: 'flex', md: 'flex' }
              }}
              disabled
            >
              Novo Produto
            </Button>
          </span>
        </Tooltip>

        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={handleImportCmv}
          sx={{
            bgcolor: "#ff9800",
            color: "white",
            fontWeight: 600,
            "&:hover": { bgcolor: "#f57c00" },
            order: { xs: -1, md: 5 },
            display: { xs: 'flex', md: 'flex' }
          }}
        >
          Importar CMVs
        </Button>

        <Box sx={{
          display: 'flex',
          gap: 1,
          bgcolor: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: 1.5,
          p: 0.5,
          order: { xs: 0, md: 0 }
        }}>
          {/* Botões de visualização (grid/lista) */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="Modo de visualização"
            size="small"
            sx={{
              '.MuiToggleButton-root': {
                border: 'none',
                py: 0.5,
                '&.Mui-selected': {
                  bgcolor: '#fff3e0',
                  color: '#ff9800',
                }
              }
            }}
          >
            <ToggleButton value="grid" aria-label="grid">
              <Tooltip title="Cards">
                <GridViewIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="list" aria-label="list">
              <Tooltip title="Lista detalhada">
                <ViewListIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Menu de ações em lote */}
        <Tooltip title="Em Desenvolvimento" arrow>
          <span>
            <Button
              variant="outlined"
              endIcon={<ArrowDropDownIcon />}
              onClick={(e) => setBatchActionAnchorEl(e.currentTarget)}
              sx={{
                borderColor: '#e0e0e0',
                color: 'black',
                bgcolor: 'white',
                '&:hover': {
                  borderColor: '#ff9800',
                  bgcolor: '#fff3e0'
                },
                order: { xs: 2, md: 2 },
                display: { xs: 'none', md: 'flex' }
              }}
              disabled
            >
              Ações
            </Button>
          </span>
        </Tooltip>
        <Menu
          anchorEl={batchActionAnchorEl}
          open={Boolean(batchActionAnchorEl)}
          onClose={() => setBatchActionAnchorEl(null)}
          PaperProps={{
            elevation: 2,
            sx: { borderRadius: 2, minWidth: 200 }
          }}
        >
          <MenuItem onClick={() => handleBatchAction('publish')}>
            <ListItemIcon>
              <PublishIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Publicar Selecionados</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleBatchAction('unpublish')}>
            <ListItemIcon>
              <UnpublishedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Despublicar Selecionados</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => handleBatchAction('update_price')}>
            <ListItemIcon>
              <LocalOfferIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Atualizar Preços</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleBatchAction('update_stock')}>
            <ListItemIcon>
              <InventoryIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Atualizar Estoque</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => handleBatchAction('delete')} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Excluir Selecionados</ListItemText>
          </MenuItem>
        </Menu>

        {/* Botão de ordenação */}
        <Button
          variant="outlined"
          startIcon={<SortIcon />}
          onClick={handleSortMenuOpen}
          sx={{
            borderColor: '#e0e0e0',
            color: 'black',
            bgcolor: 'white',
            '&:hover': {
              borderColor: '#ff9800',
              bgcolor: '#fff3e0'
            },
            order: { xs: 1, md: 1 }
          }}
        >
          {isMobile ? '' : 'Ordenar'}
        </Button>
        <Menu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={handleSortMenuClose}
          PaperProps={{
            elevation: 2,
            sx: { borderRadius: 2, minWidth: 180 }
          }}
        >
          <MenuItem
            onClick={() => handleSortChange('relevance')}
            selected={currentSort === 'relevance'}
          >
            <ListItemIcon>
              <SortIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Relevância</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => handleSortChange('price_asc')}
            selected={currentSort === 'price_asc'}
          >
            <ListItemIcon>
              <ArrowUpwardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Menor preço</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => handleSortChange('price_desc')}
            selected={currentSort === 'price_desc'}
          >
            <ListItemIcon>
              <ArrowDownwardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Maior preço</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => handleSortChange('recent')}
            selected={currentSort === 'recent'}
          >
            <ListItemIcon>
              <UpdateIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Mais recentes</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => handleSortChange('stock_desc')}
            selected={currentSort === 'stock_desc'}
          >
            <ListItemIcon>
              <InventoryIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Maior estoque</ListItemText>
          </MenuItem>
        </Menu>

        {/* Botão de filtro móvel */}
        {isMobile && (
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={handleDrawerToggle}
            sx={{
              borderColor: '#e0e0e0',
              color: 'black',
              bgcolor: 'white',
              '&:hover': {
                borderColor: '#f5c1a9',
                bgcolor: 'white'
              },
              order: { xs: 3, md: 3 }
            }}
          >
            Filtros
          </Button>
        )}

        {/* Opções adicionais como menu */}
        <Tooltip title="Em Desenvolvimento" arrow>
          <span>
            <Button
              variant="outlined"
              onClick={handleAdditionalMenuOpen}
              sx={{
                minWidth: 0,
                width: 40,
                height: 40,
                borderColor: '#e0e0e0',
                color: 'black',
                bgcolor: 'white',
                padding: 0,
                '&:hover': {
                  borderColor: '#ff9800',
                  bgcolor: '#fff3e0'
                },
                order: { xs: 4, md: 4 }
              }}
              disabled
            >
              <MoreVertIcon />
            </Button>
          </span>
        </Tooltip>
        <Menu
          anchorEl={additionalMenuAnchorEl}
          open={Boolean(additionalMenuAnchorEl)}
          onClose={handleAdditionalMenuClose}
          PaperProps={{
            elevation: 2,
            sx: { borderRadius: 2, minWidth: 200 }
          }}
        >
          <MenuItem onClick={handleAdditionalMenuClose}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Exportar Produtos</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleAdditionalMenuClose}>
            <ListItemIcon>
              <UploadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Importar Produtos</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleAdditionalMenuClose}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Configurações</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleAdditionalMenuClose}>
            <ListItemIcon>
              <HelpOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Ajuda</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default CatalogHeader;
